import * as React from 'react';
import * as _ from 'lodash';
import * as classnames from 'classnames';
import ReactTooltip from 'react-tooltip';
import {
    CONDITION_TYPES,
    OptionData,
    PlainTextContent,
    ProjectLanguage,
    Question,
    QuestionSummary
} from '@nexxt/common/types';
import { getMainLanguage } from '../../../../../../utils/project';
import { isEmpty } from 'lodash';
import {
    UilTrashAlt,
    UilPlusCircle,
    UilMultiply,
    UilExclamationTriangle
} from '@iconscout/react-unicons';
import Menu from '../../../../../common/Menu';
import OptionConditionRule from '../ConditionModal/OptionConditionRule';
import {
    defaultOptionCondition,
    defaultRules
} from '../../../ConditionEditor/default';
import { getConfirmationBox } from '../../../../../common/swal/Sweetalert';
import { questionErrorMessage } from '../../../../../../constants/applicationMessage';
import { INCA_VARIABLE_FUNCTION_TYPES } from '@nexxt/common/incaLogic/types';

interface ConditionOptionProps {
    languages: ProjectLanguage[];
    option: OptionData;
    question: Question;
    questionSummaries: QuestionSummary[];
    errorObject?: any;
    conditionErrorObject: any;
    showError: boolean;
    optionDisabled?: boolean;
    showProjectHealth?: boolean;
    handleOptions: (options: OptionData[]) => void;
    updateOption: (keyName: string, value: any) => void;
    updateOptionTextsByLanguage: (value: string, language: string) => void;
    deleteOption: () => Promise<void>;
    isLive?: boolean;
    existsInPreviousPackage?: boolean;
}

const ConditionOption: React.FC<ConditionOptionProps> = ({
    languages,
    option,
    question,
    questionSummaries,
    errorObject,
    conditionErrorObject,
    showError,
    optionDisabled,
    showProjectHealth,
    handleOptions,
    updateOption,
    updateOptionTextsByLanguage,
    deleteOption,
    isLive,
    existsInPreviousPackage
}: ConditionOptionProps) => {
    const [touched, setTouched] = React.useState({
        label: false,
        texts: {}
    });
    const isEdit = true;
    const hideDeleteButton = isLive && existsInPreviousPackage;
    const setDirty = (keyName: string) => {
        setTouched((prevTouched) => ({ ...prevTouched, [keyName]: true }));
    };

    const addConditionRule = () => {
        const newRule = defaultRules();
        let newConditions = _.cloneDeep(option.conditions);
        if (newConditions?.length) {
            newConditions[newConditions.length - 1].rules.push(newRule);
        } else {
            newConditions = [defaultOptionCondition()];
        }

        updateOption('conditions', newConditions);
    };

    const handleQuestionSelect = (
        conditionIndex: number,
        ruleIndex: number,
        questionSummary
    ) => {
        const newConditions = _.cloneDeep(option.conditions);
        const currentCondition = newConditions[conditionIndex];
        currentCondition.rules[ruleIndex] = {
            ...currentCondition.rules[ruleIndex],
            selectedReferenceQ: {
                id: questionSummary.id,
                label: questionSummary.label,
                value: questionSummary.value
            },
            compareOptions: []
        };
        newConditions[conditionIndex] = currentCondition;
        updateOption('conditions', newConditions);
    };

    const handleOptionSelect = (
        conditionIndex: number,
        ruleIndex: number,
        type: 'options' | 'number' | 'string' | 'boolean',
        value,
        compareType?: CONDITION_TYPES
    ) => {
        console.log('OPTION: ', option);
        const newConditions = _.cloneDeep(option.conditions);
        const currentCondition = newConditions[conditionIndex];
        switch (type) {
            case 'options':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareOptions: value.map((opt) => ({
                        id: opt.id,
                        label: opt.label,
                        optionKey: opt.optionKey,
                        value: opt.value
                    }))
                };
                currentCondition.rules[ruleIndex].compareType = compareType;
                break;
            case 'number':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareNumber: value
                };
                currentCondition.rules[ruleIndex].compareType = compareType;

                break;
            case 'string':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareCustomValue: value
                };
                currentCondition.rules[ruleIndex].compareType = compareType;
                break;
            case 'boolean':
                currentCondition.rules[ruleIndex] = {
                    ...currentCondition.rules[ruleIndex],
                    compareType: compareType || CONDITION_TYPES.CUSTOM_VALUE
                };
                break;
        }

        newConditions[conditionIndex] = currentCondition;
        updateOption('conditions', newConditions);
    };

    const handleOperatorSelect = (
        conditionIndex: number,
        ruleIndex: number,
        selectedOperator
    ) => {
        console.log('SELECTED OPERATOR : ', selectedOperator);
        const newConditions = _.cloneDeep(option.conditions);
        const currentCondition = newConditions[conditionIndex];
        currentCondition.rules[ruleIndex] = {
            ...currentCondition.rules[ruleIndex],
            selectedOperator
        };

        if (
            [
                INCA_VARIABLE_FUNCTION_TYPES.IS_DISPLAYED,
                INCA_VARIABLE_FUNCTION_TYPES.IS_NOT_DISPLAYED
            ].includes(selectedOperator?.type)
        ) {
            currentCondition.rules[ruleIndex] = {
                ...currentCondition.rules[ruleIndex],
                compareType: CONDITION_TYPES.CUSTOM_VALUE
            };
        }

        newConditions[conditionIndex] = currentCondition;
        updateOption('conditions', newConditions);
    };

    const removeConditionRule = async (conditionIndex, ruleIndex) => {
        const { isConfirmed } = await getConfirmationBox({
            message: ' This action cannot be undone.',
            showCloseButton: true,
            title: 'Delete Condition',
            messageAligned: 'left'
        });
        if (isConfirmed) {
            let newConditions = _.cloneDeep(option.conditions);
            const currentCondition = newConditions[conditionIndex];
            const currentRuleId = currentCondition.rules[ruleIndex]?.ruleID;
            const currentConditionId = currentCondition.conditionID;

            currentCondition.rules = [
                ...currentCondition.rules.slice(0, ruleIndex),
                ...currentCondition.rules.slice(ruleIndex + 1)
            ];
            currentRuleId &&
                (currentCondition.deletedRuleIDs = [
                    ...(currentCondition.deletedRuleIDs || []),
                    currentRuleId
                ]);

            newConditions[conditionIndex] = currentCondition;

            if (!currentCondition.rules?.length) {
                newConditions = [
                    ...newConditions.slice(0, conditionIndex),
                    ...newConditions.slice(conditionIndex + 1)
                ];
                currentConditionId &&
                    updateOption('deleteConditionIDs', [
                        ...(option.deleteConditionIDs || []),
                        currentConditionId
                    ]);
            }
            updateOption('conditions', newConditions);
        }
    };

    const switchConditionRule = (conditionIndex, ruleIndex, type) => {
        let newConditions = _.cloneDeep(option.conditions);
        const currentCondition = newConditions[conditionIndex];
        if (type.value === 'SWITCH_TO_CONDITION') {
            const deletedRuleIDs = [];
            const rules: any = [...currentCondition.rules.slice(ruleIndex)].map(
                (rule) => {
                    delete rule.conditionID;
                    rule?.ruleID && deletedRuleIDs.push(rule.ruleID);
                    return { ...rule, ruleID: 0, new: true };
                }
            );
            currentCondition.rules = [
                ...currentCondition.rules.slice(0, ruleIndex)
            ];
            currentCondition.deletedRuleIDs = [
                ...(currentCondition?.deletedRuleIDs || []),
                ...deletedRuleIDs
            ];
            const nextCondition = newConditions[conditionIndex + 1] || {
                conditionID: 0,
                isEdit: true,
                new: true,
                rules: []
            };

            nextCondition.rules.push(...rules);
            newConditions[conditionIndex + 1] = nextCondition;
        } else {
            const currentRules: any = currentCondition.rules
                .slice()
                .map((rule) => {
                    delete rule?.conditionID;
                    return { ...rule, ruleID: 0, new: true };
                });
            currentCondition?.conditionID &&
                updateOption('deleteConditionIDs', [
                    ...(option.deleteConditionIDs || []),
                    currentCondition.conditionID
                ]);

            const prevCondition = newConditions[conditionIndex - 1];

            prevCondition.rules = [...prevCondition.rules, ...currentRules];

            newConditions[conditionIndex - 1] = prevCondition;
            newConditions = [
                ...newConditions.slice(0, conditionIndex),
                ...newConditions.slice(conditionIndex + 1)
            ];
        }

        updateOption('conditions', newConditions);
    };

    const renderCondition = () => {
        const render = (option?.conditions as any)?.map(
            (condition, conditionIndex) => {
                const c = condition.rules.map((rule, ruleIndex) => {
                    return (
                        <>
                            {!(ruleIndex === 0 && conditionIndex === 0) && (
                                <>
                                    {isEdit && (
                                        <div className="condition--rule--separator">
                                            <span
                                                className="condition--rule--remove"
                                                onClick={() =>
                                                    removeConditionRule(
                                                        conditionIndex,
                                                        ruleIndex
                                                    )
                                                }
                                            >
                                                <UilMultiply size={20} />
                                            </span>
                                        </div>
                                    )}
                                    {isEdit ? (
                                        <div className="condition--rule--rel is-edit">
                                            <Menu
                                                items={[
                                                    {
                                                        label: 'and',
                                                        value: 'SWITCH_TO_RULE'
                                                    },
                                                    {
                                                        label: 'or',
                                                        value: 'SWITCH_TO_CONDITION'
                                                    }
                                                ]}
                                                selectedItem={
                                                    ruleIndex
                                                        ? 'SWITCH_TO_RULE'
                                                        : 'SWITCH_TO_CONDITION'
                                                }
                                                onClickItem={(value) =>
                                                    switchConditionRule(
                                                        conditionIndex,
                                                        ruleIndex,
                                                        value
                                                    )
                                                }
                                                disabled={
                                                    !isEdit || optionDisabled
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <div className="condition--rule--rel">
                                            {ruleIndex ? 'AND' : 'OR'}
                                        </div>
                                    )}
                                </>
                            )}
                            <OptionConditionRule
                                className="option--condition"
                                key={`rule-${option.value}-${conditionIndex}-${ruleIndex}`}
                                isEdit={isEdit}
                                rule={rule}
                                showError={showError}
                                questionSummaries={questionSummaries}
                                handleQuestionSelect={(questionSummary) =>
                                    handleQuestionSelect(
                                        conditionIndex,
                                        ruleIndex,
                                        questionSummary
                                    )
                                }
                                handleOptionSelect={(
                                    type,
                                    option,
                                    compareType
                                ) =>
                                    handleOptionSelect(
                                        conditionIndex,
                                        ruleIndex,
                                        type,
                                        option,
                                        compareType
                                    )
                                }
                                handleOperatorSelect={(operator) =>
                                    handleOperatorSelect(
                                        conditionIndex,
                                        ruleIndex,
                                        operator
                                    )
                                }
                                errorObject={
                                    (conditionErrorObject || {})?.[
                                        conditionIndex
                                    ]?.[ruleIndex]
                                }
                                disabled={optionDisabled}
                                option={option}
                            />
                        </>
                    );
                });
                return c;
            }
        );

        return (
            <>
                {render}
                {isEdit && !optionDisabled && (
                    <div
                        className="condition--rule--row d-flex justify-content-center add-button"
                        onClick={addConditionRule}
                    >
                        <UilPlusCircle size={20} /> Add Another Rule
                    </div>
                )}
            </>
        );
    };
    return (
        <div
            className={classnames('option--container text text--1 condition', {
                error:
                    showError &&
                    (errorObject?.isError || conditionErrorObject?.isError)
            })}
            style={{ width: `calc(100% - 8px)` }}
        >
            <div className="option--column">
                <div className="option--row">
                    <div className="option--row--title">Display Text:</div>
                </div>
                <div className="option--row">
                    <div className="option--row--wrapper column">
                        {languages?.map((language, index) => {
                            const languageCode = language.value;
                            const texts =
                                (
                                    option.texts.find(
                                        (text) => text.language === languageCode
                                    )?.contents?.[0] as PlainTextContent
                                )?.text || '';

                            const errorPadding =
                                (touched?.texts?.[languageCode] || showError) &&
                                (errorObject?.texts || {})[languageCode]
                                    ? 26
                                    : 0;
                            const langPadding = languages.length > 1 ? 26 : 0;
                            return (
                                <div
                                    className="option--text--wrapper"
                                    key={`${option?.value}-${language.value}`}
                                >
                                    <input
                                        placeholder="Option text here..."
                                        className={classnames('option--text', {
                                            error:
                                                (touched?.texts[languageCode] ||
                                                    showError) &&
                                                (errorObject?.texts || {})[
                                                    languageCode
                                                ]
                                        })}
                                        style={{
                                            paddingRight:
                                                langPadding + errorPadding
                                        }}
                                        type="text"
                                        value={texts}
                                        onBlur={() =>
                                            setTouched((touched) => ({
                                                ...touched,
                                                texts: {
                                                    ...touched.texts,
                                                    [languageCode]: true
                                                }
                                            }))
                                        }
                                        onChange={(e) => {
                                            setTouched((touched) => ({
                                                ...touched,
                                                texts: {
                                                    ...touched.texts,
                                                    [languageCode]: true
                                                }
                                            }));
                                            updateOptionTextsByLanguage(
                                                e.target.value,
                                                languageCode
                                            );
                                        }}
                                        disabled={optionDisabled}
                                    />
                                    <div className="option--text--inner">
                                        {languages.length > 1 && (
                                            <span
                                                className={classnames(
                                                    'option--text--lang',
                                                    {
                                                        error:
                                                            (touched.texts[
                                                                languageCode
                                                            ] ||
                                                                showError) &&
                                                            errorObject?.texts &&
                                                            errorObject
                                                                ?.texts?.[
                                                                languageCode
                                                            ]
                                                    }
                                                )}
                                            >
                                                {languageCode}
                                            </span>
                                        )}
                                        {(touched?.texts[languageCode] ||
                                            showError) &&
                                            (errorObject?.texts || {})[
                                                languageCode
                                            ] && (
                                                <>
                                                    <ReactTooltip
                                                        className="tooltip"
                                                        id={`option-${option.value}-text-${languageCode}-error`}
                                                        ara-haspopup="true"
                                                        effect="solid"
                                                        place="top"
                                                    >
                                                        <p>
                                                            {
                                                                questionErrorMessage.REQUIRED
                                                            }
                                                        </p>
                                                    </ReactTooltip>
                                                    <span
                                                        data-tip="custom show"
                                                        data-for={`option-${option.value}-text-${languageCode}-error`}
                                                        data-delay-show="500"
                                                        className="option--text--error"
                                                    >
                                                        <UilExclamationTriangle
                                                            size={16}
                                                        />
                                                    </span>
                                                </>
                                            )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="option--row">
                    <div className="option--row--title">Reference Label:</div>
                    <div
                        className={classnames(
                            'option--label--wrapper condition',
                            {
                                error:
                                    (touched.label || showError) &&
                                    errorObject?.label
                            }
                        )}
                        data-tip="custom show"
                        data-for={`option-${option.value}-label-helper`}
                        data-delay-show="500"
                    >
                        <span className="option--label--hash">#</span>
                        <input
                            className={classnames('option--label condition', {
                                error:
                                    (touched.label || showError) &&
                                    errorObject?.label
                            })}
                            placeholder="option text label"
                            type="text"
                            value={option?.label}
                            onBlur={() => {
                                setDirty('label');
                                if (isEmpty(option?.label)) {
                                    const l = getMainLanguage(languages);
                                    const mainLanguageIndex =
                                        option.texts.findIndex(
                                            (t) => t.language == l
                                        );

                                    updateOption(
                                        'label',
                                        (
                                            option.texts?.[mainLanguageIndex]
                                                ?.contents?.[0] as PlainTextContent
                                        ).text || ''
                                    );
                                }
                            }}
                            onChange={(e) => {
                                updateOption('label', e.target.value);
                                setDirty('label');
                            }}
                            disabled={optionDisabled}
                        />
                        <ReactTooltip
                            className="tooltip"
                            id={`option-${option.value}-label-helper`}
                            ara-haspopup="true"
                            effect="solid"
                            place="top"
                        >
                            <span>
                                Option label that is only visible to you
                            </span>
                        </ReactTooltip>
                        {(touched.label || showError) && errorObject?.label && (
                            <>
                                {' '}
                                <span
                                    data-tip="custom show"
                                    data-for={`option-${option.value}-label-error`}
                                    data-delay-show="500"
                                    className="option--text--error"
                                >
                                    <UilExclamationTriangle size={16} />
                                </span>
                                <ReactTooltip
                                    className="tooltip"
                                    id={`option-${option.value}-label-error`}
                                    ara-haspopup="true"
                                    effect="solid"
                                    place="top"
                                >
                                    {questionErrorMessage.REQUIRED}
                                </ReactTooltip>
                            </>
                        )}
                    </div>
                </div>
                <div className="option--row">
                    <div className="option--row--title">Add logic on right</div>
                </div>
                <div
                    className="option--row"
                    style={{ color: '#595959', fontSize: '12px' }}
                >
                    if the conditions set are met, this variable will be true
                </div>
            </div>
            <div className="option--column --with-border">
                {renderCondition()}
            </div>
            <div className="option--column">
                {!optionDisabled && !hideDeleteButton && (
                    <div className="option--icon delete" onClick={deleteOption}>
                        <UilTrashAlt size={16} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConditionOption;
